import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, ArrowRight, ArrowLeft, CheckCircle2, Loader2, XCircle, Mail, Check, CreditCard, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useRegion } from "../context/RegionContext";
import { UpgradePlans } from "./Member";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 20;

function formatMoney(cents, currency) {
  const value = (cents || 0) / 100;
  const code = (currency || "usd").toUpperCase();
  const hasCents = value % 1 !== 0;
  const formatted = value.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
  return `${code} $${formatted}`;
}

/* --------------------------- Membership section -------------------------- */
function MembershipTiles() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    axios.get(`${API}/payments/pricing`).then(({ data }) => setPricing(data)).catch(() => setPricing(null));
  }, []);
  if (!pricing) return null;

  // Signed-in paid member — congratulate + link to manage
  if (user?.tier === "paid_member") {
    return (
      <div data-testid="shop-member-active" className="bg-emerald-50 border border-emerald-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="eyebrow text-emerald-700 mb-1">You're a member</div>
          <div className="text-slate-800 text-sm">Your Member Hub subscription is active. Manage or cancel from your account.</div>
        </div>
        <Link to="/member/account" className="btn-primary whitespace-nowrap">Manage subscription <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  // Signed-in but not paid — delegate to the existing UpgradePlans (Stripe checkout)
  if (user) {
    return (
      <div data-testid="shop-member-upgrade">
        <UpgradePlans />
      </div>
    );
  }

  // Guest — show tiles that send the visitor to signup first
  const plans = [
    { key: "member_monthly", tag: "Monthly", price: pricing.monthly.display_amount, per: "per month", note: "Cancel anytime, full access while active." },
    { key: "member_yearly", tag: "Annual", price: pricing.yearly.display_amount, per: "per year", note: `Save ${pricing.yearly.savings_pct}% versus monthly.`, featured: true },
  ];
  return (
    <div data-testid="shop-member-guest-tiles" className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {plans.map((p) => (
        <div key={p.key} data-testid={`shop-plan-${p.key}`} className={`p-6 border ${p.featured ? "border-brand-orange bg-white" : "border-slate-200 bg-white"} relative`}>
          {p.featured && <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-widest font-bold bg-brand-orange text-white px-2 py-1">Best value</span>}
          <div className="eyebrow text-brand-orange mb-3">{p.tag}</div>
          <div className="flex items-baseline gap-1 mb-3">
            <span className="font-serif text-4xl text-brand-blue">{p.price}</span>
            <span className="text-sm text-slate-500 ml-1">{p.per}</span>
          </div>
          <p className="text-xs text-slate-600 mb-5">{p.note}</p>
          <ul className="text-xs text-slate-700 space-y-2 mb-6">
            <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-orange mt-0.5 flex-shrink-0" /> Every editable template and tool</li>
            <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-orange mt-0.5 flex-shrink-0" /> New resources added quarterly</li>
            <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-orange mt-0.5 flex-shrink-0" /> Practitioner forum access</li>
          </ul>
          <Link to="/register" className={`w-full justify-center inline-flex items-center gap-2 px-5 py-3 font-semibold text-sm ${p.featured ? "bg-brand-orange text-white" : "border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white transition-colors"}`}>
            <CreditCard className="w-4 h-4" /> Join and choose {p.tag}
          </Link>
        </div>
      ))}
    </div>
  );
}


/* ------------------------------ Product card ----------------------------- */
function ProductCard({ p, variant = "book" }) {
  const isCoaching = variant === "coaching";
  const ctaLabel = p.cta_label || (isCoaching ? "Explore this offer" : "View details");
  return (
    <Link
      to={`/shop/${p.slug}`}
      data-testid={`shop-product-${p.slug}`}
      className="group border border-slate-200 hover:border-brand-orange transition-colors overflow-hidden flex flex-col bg-white h-full"
    >
      {!isCoaching && (
        <div className="relative h-72 bg-slate-100 overflow-hidden flex items-center justify-center">
          <img src={p.cover_image} alt={p.title} className="h-full object-contain p-6 group-hover:scale-[1.03] transition-transform duration-700" />
        </div>
      )}
      <div className="p-7 lg:p-8 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-orange mb-4 leading-relaxed">{p.format}</div>
        <h3 className="font-serif text-2xl text-brand-blue mb-3 leading-snug">{p.title}</h3>
        {!isCoaching && <div className="text-sm text-slate-500 mb-3">by {p.author}</div>}
        <p className="text-sm text-slate-600 leading-relaxed mb-6">{p.short_description}</p>
        {isCoaching && p.features && p.features.length > 0 && (
          <ul className="text-sm text-slate-700 space-y-2.5 mb-6">
            {p.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <Check className="w-3.5 h-3.5 text-brand-orange mt-1 flex-shrink-0" />
                <span className="leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto pt-6 border-t border-slate-100">
          <div className="font-serif text-2xl text-brand-blue mb-4">{formatMoney(p.price_cents, p.currency)}</div>
          <span className="text-xs uppercase tracking-widest font-semibold text-brand-orange inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
            {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}


/* ------------------------------- Shop index ------------------------------- */
export function ShopIndex() {
  const { region } = useRegion();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/shop/products?region=${region}`)
      .then(({ data }) => { setProducts(data.products || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [region]);

  const books = products.filter((p) => p.kind !== "coaching");
  const coaching = products.filter((p) => p.kind === "coaching");

  return (
    <div data-testid="shop-index-page">
      <Seo
        path="/shop"
        title="Shop - Books, Strategic Advisory & Member Hub"
        description="Buy the SIA practitioner book, book a Strategic Advisory session with Andrea Kanaris, or join the Member Resource Hub. AU & US pricing."
      />
      <section className="relative bg-brand-blue text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-brand-blue to-brand-blue-deep" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow text-brand-yellow mb-6"><ShoppingBag className="w-4 h-4 inline mr-2" />Shop</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Membership, coaching and practical resources.</h1>
            <p className="text-lg lg:text-xl text-white/85 mt-6 leading-relaxed">
              Professional tools and support for social impact practitioners. Access the Member Hub, book one-to-one coaching with Andrea Kanaris, or purchase A Practical Guide to Social Impact Assessment for the Real World.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-8">
            <div className="eyebrow text-brand-orange mb-2">Member Hub subscription</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-3">Membership access.</h2>
            <p className="text-slate-600 leading-relaxed">
              Twelve modules, every editable template, the practitioner forum and the Certificate of Completion for the full curriculum. Two ways to join.
            </p>
          </div>
          <MembershipTiles />
        </div>
      </section>

      {coaching.length > 0 && (
        <section data-testid="shop-coaching-section" className="py-24 bg-white border-b border-slate-100">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-12">
              <div className="eyebrow text-brand-orange mb-3">Work directly with Andrea</div>
              <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-4">Strategic advisory, tailored to the challenge.</h2>
              <p className="text-slate-600 leading-relaxed">
                Choose from a focused strategic session, a three-session series or an ongoing advisory partnership - each designed to help you think clearly, work through complex challenges and translate insight into practical action.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
              {coaching.map((p) => <ProductCard key={p.slug} p={p} variant="coaching" />)}
            </div>

            {/* Enterprise advisory panel */}
            <div data-testid="shop-enterprise-panel" className="mt-14 bg-brand-blue text-white overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr]">
                <div className="p-10 lg:p-14 border-b lg:border-b-0 lg:border-r border-white/10">
                  <div className="eyebrow text-brand-orange mb-4 inline-flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> For organisations and teams
                  </div>
                  <h3 className="font-serif text-3xl lg:text-4xl leading-[1.1] mb-5">Enterprise advisory and capability support.</h3>
                  <p className="text-white/85 leading-relaxed mb-4">
                    Tailored advisory, coaching and capability programs for organisations seeking to strengthen internal practice, support project teams or build capability across a business unit or professional cohort.
                  </p>
                  <p className="text-white/70 leading-relaxed mb-8 text-sm">
                    Programs can be designed around organisational priorities, project context, participant needs and preferred delivery format.
                  </p>
                  <div className="eyebrow text-brand-yellow mb-4">Support may include</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {[
                      "Executive and leadership briefings",
                      "Individual or group advisory sessions",
                      "Team coaching and mentoring",
                      "Facilitated workshops and project clinics",
                      "Tailored training programs",
                      "Review of live project work, systems or documentation",
                      "Tools, templates and practical frameworks",
                      "Capability assessments and development plans",
                      "Ongoing support across project phases",
                      "Online, in-person or blended delivery",
                    ].map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-white/85">
                        <Check className="w-4 h-4 text-brand-orange mt-0.5 flex-shrink-0" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-10 lg:p-14 bg-brand-blue-deep flex flex-col justify-between">
                  <div>
                    <div className="eyebrow text-brand-yellow mb-4">Commercial treatment</div>
                    <div className="font-serif text-3xl leading-tight text-white mb-3">Tailored scope and pricing.</div>
                    <p className="text-white/75 text-sm leading-relaxed mb-8">
                      Scope, delivery format and pricing are shaped in an initial discussion. Every enterprise program is designed around your team, timelines and outcomes.
                    </p>
                  </div>
                  <div>
                    <Link
                      to="/enterprise-inquiry"
                      data-testid="shop-enterprise-cta"
                      className="w-full justify-center inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-4 font-semibold hover:bg-orange-600 transition-colors"
                    >
                      Contact us about enterprise support <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Distinction from Corporate Project Advisory */}
            <div data-testid="shop-discuss-crosslink" className="mt-6 bg-slate-50 border border-slate-200 p-6 lg:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-sm text-slate-700 leading-relaxed max-w-2xl">
                Looking for specialist consulting, due diligence, independent review or support for a defined project?
              </div>
              <Link to="/contact" className="btn-secondary whitespace-nowrap" data-testid="shop-discuss-project-link">
                Contact Us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-24 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-10">
            <div className="eyebrow text-brand-orange mb-2">Books &amp; resources</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight">One-off purchases.</h2>
          </div>
          {loading && <div className="text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading products...</div>}
          {!loading && books.length === 0 && (
            <div data-testid="shop-empty" className="text-slate-500">No products available right now.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {books.map((p) => <ProductCard key={p.slug} p={p} variant="book" />)}
          </div>
        </div>
      </section>
    </div>
  );
}


/* ------------------------------ Product detail ---------------------------- */
export function ShopProduct() {
  const { slug } = useParams();
  const { region } = useRegion();
  const [product, setProduct] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/shop/products/${slug}?region=${region}`)
      .then(({ data }) => { if (alive) { setProduct(data); setState({ loading: false, error: null }); } })
      .catch((e) => {
        if (!alive) return;
        setState({ loading: false, error: e?.response?.status === 404 ? "Product not found." : "Could not load this product." });
      });
    return () => { alive = false; };
  }, [slug, region]);

  const isCoaching = product?.kind === "coaching";

  const buy = async () => {
    if (!buyerEmail || !buyerEmail.includes("@")) {
      alert("Please enter a valid email address so we can send your confirmation.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/shop/checkout`, {
        product_slug: slug,
        buyer_email: buyerEmail.trim(),
        buyer_name: buyerName.trim(),
        origin_url: window.location.origin,
        region,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start checkout. Please try again.");
    } finally { setBusy(false); }
  };

  if (state.loading) return <div className="min-h-[50vh] py-16 px-6 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading...</div>;
  if (state.error) return (
    <div className="min-h-[50vh] py-16 px-6">
      <div className="max-w-2xl mx-auto text-slate-600">
        <Link to="/shop" className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Back to shop</Link>
        <div className="border-l-4 border-red-500 bg-red-50 p-5">{state.error}</div>
      </div>
    </div>
  );

  return (
    <div data-testid={`shop-product-detail-${product.slug}`} className="bg-white py-16 px-6">
      <div className="max-w-[1200px] mx-auto">
        <Link to="/shop" className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange inline-flex items-center gap-1.5 mb-8"><ArrowLeft className="w-3.5 h-3.5" /> Back to shop</Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className={`${isCoaching ? "bg-brand-blue text-white" : "bg-slate-100"} flex items-center justify-center p-10`}>
            {isCoaching ? (
              <div className="text-center max-w-sm">
                <div className="eyebrow text-brand-yellow mb-4">Coaching with Andrea</div>
                <div className="font-serif text-3xl lg:text-4xl leading-tight mb-4">{product.title}</div>
                <div className="text-white/80 text-sm">{product.format}</div>
              </div>
            ) : (
              <img src={product.cover_image} alt={product.title} className="w-full max-w-md object-contain" />
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-brand-orange mb-3">{product.format}</div>
            <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-3">{product.title}</h1>
            <div className="text-slate-500 mb-6">by {product.author}</div>
            <p className="text-slate-700 leading-relaxed mb-6">{product.short_description}</p>
            <p className="text-slate-600 leading-relaxed mb-6 text-sm">{product.long_description}</p>

            {isCoaching && product.features && product.features.length > 0 && (
              <div className="mb-6">
                <div className="eyebrow text-brand-orange mb-3">What's included</div>
                <ul className="space-y-2.5">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-brand-orange mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border-t border-slate-200 pt-8">
              <div className="text-3xl font-serif text-brand-blue mb-6">{formatMoney(product.price_cents, product.currency)}</div>

              <label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Your name</label>
              <input
                data-testid="shop-buyer-name"
                type="text" value={buyerName} onChange={(e) => setBuyerName(e.target.value)}
                placeholder="Full name"
                className="w-full border border-slate-300 px-4 py-3 mb-4 focus:outline-none focus:border-brand-blue text-sm"
              />
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Email (order confirmation)</label>
              <input
                data-testid="shop-buyer-email"
                type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full border border-slate-300 px-4 py-3 mb-6 focus:outline-none focus:border-brand-blue text-sm"
              />
              <button
                data-testid="shop-buy-btn"
                onClick={buy}
                disabled={busy}
                className="w-full justify-center inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-4 font-semibold hover:bg-orange-600 transition-colors"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting secure checkout...</> : <>{product.book_cta || (isCoaching ? "Book now" : "Buy now")} <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-xs text-slate-500 mt-3">
                Secure Stripe checkout.
                {isCoaching ? " " : " You'll enter your shipping address on the next step. "}
                {product.fulfillment_note}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ------------------------------ Order success ----------------------------- */
export function ShopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null }); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/shop/orders/${sessionId}/status`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts });
          return;
        }
        if (["failed", "expired", "not_found"].includes(data.payment_status)) {
          setState({ status: "error", data, attempts });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ status: "timeout", data, attempts });
          return;
        }
        setState({ status: "polling", data, attempts });
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch {
        if (attempts >= MAX_POLLS) { setState({ status: "error", data: null, attempts }); return; }
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="shop-success-page" className="min-h-[60vh] bg-slate-50 py-16 px-6">
      <div className="max-w-xl mx-auto bg-white border border-slate-200 p-10 shadow-sm">
        {state.status === "polling" && (
          <div data-testid="shop-success-polling" className="text-center">
            <Loader2 className="w-10 h-10 text-brand-orange animate-spin mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-brand-blue mb-2">Confirming your payment...</h1>
            <p className="text-slate-600 text-sm">Just a few seconds while Stripe confirms.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div data-testid="shop-success-paid" className="text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-brand-blue mb-3">Thank you.</h1>
            <p className="text-slate-700 mb-6">Your payment was received. {state.data?.fulfillment_note || "You'll receive an email confirmation shortly."}</p>
            <div className="bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 text-left mb-6">
              <div className="flex justify-between mb-1"><span className="text-slate-500">Product</span><span>{state.data?.product_title}</span></div>
              <div className="flex justify-between mb-1"><span className="text-slate-500">Amount</span><span>{formatMoney(state.data?.amount_cents, state.data?.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Order ID</span><span className="font-mono">{state.data?.order_id}</span></div>
            </div>
            <p className="text-xs text-slate-500 inline-flex items-center gap-2 mb-6"><Mail className="w-3.5 h-3.5" /> Confirmation sent to {state.data?.buyer_email}. Shipping updates will follow.</p>
            <button onClick={() => navigate("/shop")} className="btn-primary">Back to shop <ArrowRight className="w-4 h-4" /></button>
          </div>
        )}
        {(state.status === "error" || state.status === "timeout") && (
          <div data-testid="shop-success-error" className="text-center">
            <XCircle className="w-14 h-14 text-red-600 mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-brand-blue mb-2">We couldn't confirm your payment</h1>
            <p className="text-slate-600 text-sm mb-6">If money left your account, please email info@allthingssocialimpact.com with your order details and we'll sort it out immediately.</p>
            <button onClick={() => navigate("/shop")} className="btn-primary">Back to shop</button>
          </div>
        )}
      </div>
    </div>
  );
}
